import { useCallback, useEffect, useMemo, useState } from 'react'
import { GoogleMap, Marker } from '@react-google-maps/api'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useMapsReady } from '../hooks/useMapsReady'
import MapUnavailable from '../components/MapUnavailable'
import { GURUGRAM_CENTER, GURUGRAM_DEFAULT_ZOOM } from '../constants/geo'

type Center = {
  id: string
  name: string
  address: string
  lat?: number | string | null
  lng?: number | string | null
  max_daily_capacity?: number
  contact_phone?: string | null
  operating_hours?: Record<string, unknown> | null
  is_active: boolean
}

const PAGE_SIZE = 10

export default function ServiceCentersPage() {
  const colors = useColors()
  const [rows, setRows] = useState<Center[]>([])
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<'name' | 'max_daily_capacity' | 'created_at'>('name')
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<Center | null>(null)
  const [form, setForm] = useState({ name: '', address: '', lat: '', lng: '', max_daily_capacity: '100', contact_phone: '', operating_hours: '{}' })
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [statsById, setStatsById] = useState<Record<string, { orders_processed_today: number; capacity_utilization: number; avg_processing_time_minutes: string }>>({})

  const { keyMissing, isLoaded, loadError } = useMapsReady()

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const res = await adminApi.getServiceCenters({ search, page, limit: PAGE_SIZE, sort, order })
      const payload = res?.data ?? res
      setRows(Array.isArray(payload?.centers) ? payload.centers : [])
      setTotal(Number(payload?.total || 0))
    } catch {
      setError('Unable to load service centers.')
    } finally {
      setLoading(false)
    }
  }, [order, page, search, sort])

  useEffect(() => { void fetchRows() }, [fetchRows])

  const loadStats = useCallback(async (id: string) => {
    // Read latest state via a functional update (returning prev unchanged is a
    // no-op render) so this callback doesn't depend on statsById and get
    // recreated on every fetch.
    let alreadyLoaded = false
    setStatsById((prev) => {
      alreadyLoaded = Boolean(prev[id])
      return prev
    })
    if (alreadyLoaded) return
    try {
      const res = await adminApi.getServiceCenterStats(id)
      const payload = res?.data ?? res
      setStatsById((prev) => ({ ...prev, [id]: payload }))
    } catch (err) {
      console.warn('[ServiceCenters] failed to load capacity stats for', id, err)
    }
  }, [])

  const saveCenter = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      setFormError('Name and address are required.')
      return
    }
    // Validate coordinates so bad values don't silently drop the map marker.
    if (form.lat.trim() || form.lng.trim()) {
      const latN = Number(form.lat)
      const lngN = Number(form.lng)
      if (!form.lat.trim() || !form.lng.trim() || !Number.isFinite(latN) || !Number.isFinite(lngN)) {
        setFormError('Latitude and longitude must both be valid numbers.')
        return
      }
      if (latN < -90 || latN > 90 || lngN < -180 || lngN > 180) {
        setFormError('Latitude must be between -90 and 90, longitude between -180 and 180.')
        return
      }
    }
    setFormError('')
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
        max_daily_capacity: Number(form.max_daily_capacity || 100),
        contact_phone: form.contact_phone || undefined,
        operating_hours: JSON.parse(form.operating_hours || '{}'),
        is_active: editing ? editing.is_active : true,
      }
      if (editing?.id) await adminApi.updateServiceCenter(editing.id, payload)
      else await adminApi.createServiceCenter(payload)
      setEditing(null)
      setForm({ name: '', address: '', lat: '', lng: '', max_daily_capacity: '100', contact_phone: '', operating_hours: '{}' })
      await fetchRows()
    } catch {
      setError('Failed to save service center. Check values and try again.')
    } finally {
      setSaving(false)
    }
  }

  const pages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.is_active).length
    const atCapacity = rows.filter((r) => Number(statsById[r.id]?.capacity_utilization || 0) >= 100).length
    return { total, active, atCapacity }
  }, [rows, statsById, total])

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <h2 className="font-display" style={{ margin: 0, color: colors.textPrimary }}>Service Centers</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 12 }}>
        <StatCard label="Total" value={String(stats.total)} colors={colors} />
        <StatCard label="Active" value={String(stats.active)} colors={colors} />
        <StatCard label="At Capacity" value={String(stats.atCapacity)} colors={colors} />
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} placeholder="Search centers" style={{ flex: 1, padding: 8 }} />
          <select value={sort} onChange={(e) => setSort(e.target.value as any)}><option value="name">Name</option><option value="max_daily_capacity">Capacity</option><option value="created_at">Created</option></select>
          <select value={order} onChange={(e) => setOrder(e.target.value as any)}><option value="asc">Asc</option><option value="desc">Desc</option></select>
          <button onClick={() => { setEditing(null); setForm({ name: '', address: '', lat: '', lng: '', max_daily_capacity: '100', contact_phone: '', operating_hours: '{}' }) }}>New</button>
        </div>
        {loading ? <div style={{ padding: 20, color: colors.textMuted }}>Loading service centers...</div> : error ? <div style={{ padding: 20, color: '#b91c1c' }}>{error}</div> : rows.length === 0 ? <div style={{ padding: 20, color: colors.textMuted }}>No service centers found.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th>Name</th><th>Address</th><th>Status</th><th>Capacity</th><th>Stats</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const stat = statsById[r.id]
                return (
                  <tr key={r.id} onMouseEnter={() => { void loadStats(r.id) }}>
                    <td>{r.name}</td><td>{r.address}</td>
                    <td>{!r.is_active ? 'Inactive' : Number(stat?.capacity_utilization || 0) >= 100 ? 'At Capacity' : 'Active'}</td>
                    <td>{r.max_daily_capacity || 0}</td>
                    <td>{stat ? `${stat.orders_processed_today} today • ${stat.capacity_utilization}% util • ${stat.avg_processing_time_minutes}m avg` : 'Hover to load'}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { setEditing(r); setForm({ name: r.name, address: r.address, lat: String(r.lat || ''), lng: String(r.lng || ''), max_daily_capacity: String(r.max_daily_capacity || 100), contact_phone: r.contact_phone || '', operating_hours: JSON.stringify(r.operating_hours || {}, null, 0) }) }}>Edit</button>
                      <button onClick={async () => { await adminApi.updateServiceCenter(r.id, { is_active: !r.is_active }); await fetchRows() }}>{r.is_active ? 'Deactivate' : 'Activate'}</button>
                      <button onClick={async () => { if (confirm('Delete this service center?')) { await adminApi.deleteServiceCenter(r.id); await fetchRows() } }}>Delete</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between' }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        <h3 style={{ marginTop: 0 }}>{editing ? 'Edit Service Center' : 'Create Service Center'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" />
          <input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} placeholder="Contact Phone" />
          <input value={form.max_daily_capacity} onChange={(e) => setForm((f) => ({ ...f, max_daily_capacity: e.target.value }))} placeholder="Capacity" />
          <input type="number" step="any" value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} placeholder="Latitude" />
          <input type="number" step="any" value={form.lng} onChange={(e) => setForm((f) => ({ ...f, lng: e.target.value }))} placeholder="Longitude" />
          <input value={form.operating_hours} onChange={(e) => setForm((f) => ({ ...f, operating_hours: e.target.value }))} placeholder="Operating hours JSON" />
        </div>
        <textarea value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Address" style={{ width: '100%', marginTop: 8 }} rows={2} />
        {formError ? <p style={{ margin: '8px 0 0', color: '#B91C1C', fontSize: 13 }}>{formError}</p> : null}
        <button onClick={() => void saveCenter()} disabled={saving} style={{ marginTop: 8 }}>{saving ? 'Saving...' : 'Save'}</button>
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12, minHeight: 300 }}>
        <h3 style={{ marginTop: 0 }}>Map View</h3>
        {keyMissing ? (
          <MapUnavailable reason="keyMissing" />
        ) : loadError ? (
          <MapUnavailable reason="loadError" />
        ) : !isLoaded ? <div style={{ color: colors.textMuted }}>Map loading...</div> : (
          <GoogleMap mapContainerStyle={{ width: '100%', height: '340px' }} center={GURUGRAM_CENTER} zoom={GURUGRAM_DEFAULT_ZOOM}>
            {rows.map((r) => Number(r.lat) && Number(r.lng) ? <Marker key={r.id} position={{ lat: Number(r.lat), lng: Number(r.lng) }} title={r.name} /> : null)}
          </GoogleMap>
        )}
      </div>
    </div>
  )
}

function StatCard({ label, value, colors }: { label: string; value: string; colors: any }) {
  return <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}><div style={{ color: colors.textMuted }}>{label}</div><div style={{ color: colors.textPrimary, fontSize: 22, fontWeight: 700 }}>{value}</div></div>
}
